import { z } from 'zod'

import {
  textLimit,
  type CustomFieldDefinition,
  type CustomFieldFormValue,
  type CustomFieldFormValues,
  type CustomFieldValue,
} from './types'

/** The fields drawn under one display group; `name` is `null` for the record's own fields. */
export interface CustomFieldGroup {
  readonly name: string | null
  readonly fields: readonly CustomFieldDefinition[]
}

/** How legacy reads a dependency on a checkbox being unchecked. */
const unchecked = new Set(['', '0', 'unchecked', 'false'])

const wholeNumber = /^-?\d+$/u
/** The API stores a number as a 32-bit integer. */
const minNumber = -(2 ** 31)
const maxNumber = 2 ** 31 - 1

function groupName(definition: CustomFieldDefinition): string | null {
  const name = definition.displayGroup?.trim() ?? ''
  return name === '' ? null : name
}

/**
 * The enabled fields by display group, as legacy draws them: ungrouped fields first, then each
 * group by name, each field by its order, then its name. `only` keeps one group: `null` for the
 * ungrouped fields, or a group's name, matched ignoring case, so an owner can draw a group
 * inside one of its own sections.
 */
export function groupCustomFields(
  definitions: readonly CustomFieldDefinition[],
  only?: string | null,
): readonly CustomFieldGroup[] {
  const byName = new Map<string | null, CustomFieldDefinition[]>()
  for (const definition of definitions) {
    if (!definition.enabled) {
      continue
    }
    const name = groupName(definition)
    // Group names match ignoring case, as legacy's did; the first spelling met names the group.
    const key = name === null ? null : ([...byName.keys()].find((existing) => existing?.toLowerCase() === name.toLowerCase()) ?? name)
    byName.set(key, [...(byName.get(key) ?? []), definition])
  }

  const groups = [...byName]
    .map(([name, fields]) => ({
      name,
      fields: fields.toSorted((a, b) => a.order - b.order || a.name.localeCompare(b.name)),
    }))
    .toSorted((a, b) => (a.name === null ? -1 : b.name === null ? 1 : a.name.localeCompare(b.name)))

  if (only === undefined) {
    return groups
  }
  return groups.filter((group) =>
    only === null ? group.name === null : group.name?.toLowerCase() === only.trim().toLowerCase(),
  )
}

/** Each enabled field's form value from the record's values; a field with none starts empty. */
export function toFormValues(
  definitions: readonly CustomFieldDefinition[],
  values: readonly CustomFieldValue[],
): Record<string, CustomFieldFormValue> {
  const form: Record<string, CustomFieldFormValue> = {}
  for (const definition of definitions) {
    if (!definition.enabled) {
      continue
    }
    const value = values.find((candidate) => candidate.fieldId === definition.id)
    switch (definition.type) {
      case 'text':
      case 'dropdown':
        form[definition.id] = value?.text ?? ''
        break
      case 'number':
        form[definition.id] = value?.number === null || value?.number === undefined ? '' : String(value.number)
        break
      case 'checkbox':
        form[definition.id] = value?.checked === true
        break
      case 'checkboxList':
        form[definition.id] = value?.choices ?? []
        break
      case 'person':
        form[definition.id] = value?.personId ?? null
        break
    }
  }
  return form
}

/** The values to send for the enabled fields: those holding something, text trimmed. */
export function fromFormValues(
  definitions: readonly CustomFieldDefinition[],
  form: CustomFieldFormValues,
): readonly CustomFieldValue[] {
  const values: CustomFieldValue[] = []
  for (const definition of definitions) {
    const value = definition.enabled ? form[definition.id] : undefined
    if (value === undefined || value === null) {
      continue
    }
    const fieldId = definition.id
    switch (definition.type) {
      case 'text':
      case 'dropdown':
        if (typeof value === 'string' && value.trim() !== '') {
          values.push({ fieldId, text: value.trim() })
        }
        break
      case 'number':
        if (typeof value === 'string' && wholeNumber.test(value.trim())) {
          values.push({ fieldId, number: Number(value.trim()) })
        }
        break
      case 'checkbox':
        if (value === true) {
          values.push({ fieldId, checked: true })
        }
        break
      case 'checkboxList':
        if (Array.isArray(value) && value.length > 0) {
          values.push({ fieldId, choices: [...value] })
        }
        break
      case 'person':
        if (typeof value === 'string' && value !== '') {
          values.push({ fieldId, personId: value })
        }
        break
    }
  }
  return values
}

/**
 * The list values a dropdown offers given the form's other values: those with no dependency,
 * and those with at least one that holds, as the API checks them.
 */
export function offeredValues(
  dropdown: CustomFieldDefinition,
  definitions: readonly CustomFieldDefinition[],
  form: CustomFieldFormValues,
): readonly string[] {
  return dropdown.listValues
    .filter(
      (item) =>
        item.dependencies.length === 0 ||
        item.dependencies.some((dependency) => {
          const other = definitions.find((definition) => definition.id === dependency.fieldId && definition.enabled)
          return other !== undefined && holds(other, form[other.id], dependency.value.trim())
        }),
    )
    .map((item) => item.value)
}

function holds(field: CustomFieldDefinition, value: CustomFieldFormValue | undefined, expected: string): boolean {
  switch (field.type) {
    case 'text':
    case 'dropdown':
      return typeof value === 'string' && value.trim() === expected
    case 'number':
      // As the API compares the stored number: `01` is `1`.
      return typeof value === 'string' && wholeNumber.test(value.trim()) && String(Number(value.trim())) === expected
    case 'checkbox':
      return unchecked.has(expected.toLowerCase()) ? value !== true : value === true
    case 'checkboxList':
      return Array.isArray(value) && value.includes(expected)
    case 'person':
      // As legacy, and the API, no dependency reads a person field.
      return false
  }
}

function fieldSchema(definition: CustomFieldDefinition): z.ZodType<CustomFieldFormValue> {
  const required = definition.required
  switch (definition.type) {
    case 'text': {
      const limit = definition.maxLength ?? textLimit
      return z
        .string()
        .refine((text) => !required || text.trim() !== '', 'customFields.error.required')
        .refine((text) => text.trim().length <= limit, 'customFields.error.tooLong')
    }
    case 'number':
      return z
        .string()
        .refine((text) => !required || text.trim() !== '', 'customFields.error.required')
        .refine((text) => {
          const trimmed = text.trim()
          return trimmed === '' || (wholeNumber.test(trimmed) && Number(trimmed) >= minNumber && Number(trimmed) <= maxNumber)
        }, 'customFields.error.number')
    case 'checkbox':
      return z.boolean().refine((checked) => !required || checked, 'customFields.error.check')
    case 'dropdown':
      return z.string().refine((text) => !required || text !== '', 'customFields.error.choose')
    case 'checkboxList':
      return z
        .array(z.string())
        .refine((choices) => !required || choices.length > 0, 'customFields.error.chooseSome')
        .refine((choices) => new Set(choices).size === choices.length, 'customFields.error.repeated')
    case 'person':
      return z
        .string()
        .nullable()
        .refine((person) => !required || (person !== null && person !== ''), 'customFields.error.choosePerson')
  }
}

/**
 * The form schema for the enabled fields' values, by field id, keeping the rules the API checks:
 * required, maximum length, whole numbers, and dropdown and checkbox list values among those
 * offered. Its messages are catalog ids. Nest it in the record's schema, as
 * `customFields: customFieldsSchema(fields)`.
 */
export function customFieldsSchema(definitions: readonly CustomFieldDefinition[]) {
  const enabled = definitions.filter((definition) => definition.enabled)
  const shape = Object.fromEntries(enabled.map((definition) => [definition.id, fieldSchema(definition)]))
  return z.object(shape).superRefine((form, context) => {
    for (const definition of enabled) {
      const value = form[definition.id]
      const offered =
        definition.type === 'dropdown'
          ? offeredValues(definition, definitions, form)
          : definition.listValues.map((item) => item.value)
      const notOffered =
        (definition.type === 'dropdown' && typeof value === 'string' && value !== '' && !offered.includes(value)) ||
        (definition.type === 'checkboxList' && Array.isArray(value) && value.some((choice) => !offered.includes(choice)))
      if (notOffered) {
        context.addIssue({ code: 'custom', path: [definition.id], message: 'customFields.error.notOffered' })
      }
    }
  })
}
